import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  Send, 
  MessageCircle, 
  User, 
  Clock,
  ArrowLeft,
  Trophy,
  Target
} from 'lucide-react';

// Types
interface User {
  id: string;
  name: string;
  accountType: 'student' | 'public';
  gradeLevel?: number;
  university?: string;
  rating: number;
  wins: number;
  totalMatches: number;
  avatar?: string;
  profilePicture?: string;
  isOnline: boolean;
}

interface Competitor extends User {
  lastSeen?: string;
  currentStake?: number;
}

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'opponent' | 'system';
  timestamp: Date;
}

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 20px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 15px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const CompetitorInfo = styled.div`
  flex: 1;
`;

const CompetitorName = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  margin-bottom: 5px;
`;

const CompetitorDetails = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OnlineStatus = styled.div<{ isOnline: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.isOnline ? '#4caf50' : '#9e9e9e'};
`;

const ChatBody = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 400px;
`;

const MessageContainer = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  margin-bottom: 10px;
`;

const MessageBubble = styled.div<{ isUser: boolean; isSystem: boolean }>`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  background: ${props => {
    if (props.isSystem) return 'rgba(255, 255, 255, 0.1)';
    return props.isUser 
      ? 'linear-gradient(135deg, #667eea, #764ba2)' 
      : 'rgba(255, 255, 255, 0.1)';
  }};
  color: white;
  border: ${props => props.isSystem ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'};
  text-align: ${props => props.isSystem ? 'center' : 'left'};
  font-style: ${props => props.isSystem ? 'italic' : 'normal'};
  font-size: 0.9rem;
  line-height: 1.4;
`;

const MessageTime = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 5px;
  text-align: center;
`;

const ChallengeCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  margin: 20px;
  text-align: center;
`;

const ChallengeTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ChallengeDescription = styled.div`
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ChallengeButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
`;

const InputContainer = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  gap: 10px;
`;

const MessageInput = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 12px 20px;
  color: white;
  font-size: 0.9rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SendButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(45deg, #667eea, #764ba2)'
  };
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 50%;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const TypingIndicator = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  font-style: italic;
  text-align: center;
  padding: 10px;
`;

const ChatModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  competitor?: Competitor;
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  onStartChallenge: () => void;
}> = ({ isOpen, onClose, competitor, messages, onSendMessage, onStartChallenge }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Simulate typing indicator
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].sender === 'user') {
      setIsTyping(true);
      const timer = setTimeout(() => {
        setIsTyping(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen || !competitor) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <BackButton onClick={onClose}>
            <ArrowLeft size={20} />
          </BackButton>
          <CompetitorInfo>
            <CompetitorName>{competitor.name}</CompetitorName>
            <CompetitorDetails>
              <OnlineStatus isOnline={competitor.isOnline} />
              {competitor.accountType === 'student' 
                ? `Grade ${competitor.gradeLevel} • Rating: ${competitor.rating}`
                : `${competitor.university} • Rating: ${competitor.rating}`
              }
              
              {/* Enhanced Educational Information */}
              {competitor.educationalInfo && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  {competitor.educationalInfo.isCurrentlyStudying && competitor.educationalInfo.institution && competitor.educationalInfo.courseOfStudy && competitor.educationalInfo.yearOfStudy ? (
                    `${competitor.educationalInfo.yearOfStudy === 1 ? '1st' : 
                      competitor.educationalInfo.yearOfStudy === 2 ? '2nd' : 
                      competitor.educationalInfo.yearOfStudy === 3 ? '3rd' : 
                      competitor.educationalInfo.yearOfStudy + 'th'} Year ${competitor.educationalInfo.courseOfStudy}`
                  ) : competitor.educationalInfo.institution ? (
                    `Studying at ${competitor.educationalInfo.institution}`
                  ) : null}
                </div>
              )}
              
              {/* Location Information */}
              {competitor.personalInfo && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  📍 {competitor.personalInfo.city || competitor.personalInfo.province || competitor.personalInfo.region || competitor.personalInfo.country}
                </div>
              )}
            </CompetitorDetails>
          </CompetitorInfo>
        </ModalHeader>

        <ChatBody>
          {messages.length === 0 && (
            <ChallengeCard>
              <ChallengeTitle>
                <Trophy size={20} />
                Challenge Invitation
              </ChallengeTitle>
              <ChallengeDescription>
                Send a message to challenge {competitor.name} to a competition. 
                Choose your category, mode, and stake amount to begin!
              </ChallengeDescription>
              <ChallengeButton onClick={onStartChallenge}>
                Start Challenge
              </ChallengeButton>
            </ChallengeCard>
          )}

          {messages.map((message) => (
            <MessageContainer key={message.id} isUser={message.sender === 'user'}>
              <MessageBubble 
                isUser={message.sender === 'user'} 
                isSystem={message.sender === 'system'}
              >
                {message.text}
                <MessageTime>{formatTime(message.timestamp)}</MessageTime>
              </MessageBubble>
            </MessageContainer>
          ))}

          {isTyping && (
            <TypingIndicator>
              {competitor.name} is typing...
            </TypingIndicator>
          )}

          <div ref={messagesEndRef} />
        </ChatBody>

        <InputContainer>
          <MessageInput
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
          />
          <SendButton 
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
          >
            <Send size={20} />
          </SendButton>
        </InputContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ChatModal;
